export const STARTER_GENERATOR_VERSION = '0.1.0';

export const STANDARD_ARRAY = Object.freeze([15, 14, 13, 12, 10, 8]);

export const ABILITY_ORDER = Object.freeze([
  'strength',
  'dexterity',
  'constitution',
  'intelligence',
  'wisdom',
  'charisma'
]);

export const SKILL_ABILITY_MAP = Object.freeze({
  Athletics: 'strength',
  Acrobatics: 'dexterity',
  'Sleight of Hand': 'dexterity',
  Stealth: 'dexterity',
  Arcana: 'intelligence',
  History: 'intelligence',
  Investigation: 'intelligence',
  Nature: 'intelligence',
  Religion: 'intelligence',
  'Animal Handling': 'wisdom',
  Insight: 'wisdom',
  Medicine: 'wisdom',
  Perception: 'wisdom',
  Survival: 'wisdom',
  Deception: 'charisma',
  Intimidation: 'charisma',
  Performance: 'charisma',
  Persuasion: 'charisma'
});

export const STARTER_CLASS_OPTIONS = Object.freeze({
  barbarian: {
    key: 'barbarian',
    label: 'Barbarian',
    templateKey: 'barbarian',
    hitDie: 12,
    primaryAbilities: ['strength', 'constitution'],
    secondaryAbilities: ['dexterity', 'wisdom'],
    savingThrows: ['strength', 'constitution'],
    skillChoices: ['Animal Handling', 'Athletics', 'Intimidation', 'Nature', 'Perception', 'Survival'],
    skillCount: 2,
    armorProficiencies: ['light armor', 'medium armor', 'shields'],
    weaponProficiencies: ['simple weapons', 'martial weapons'],
    tools: [],
    spellcastingAbility: null,
    levelOneFeatures: [
      { name: 'Rage', description: 'Enter a battle rage to deal extra melee damage and shrug off punishment.' },
      { name: 'Unarmored Defense', description: 'While not wearing armor, your AC can be based on Dexterity and Constitution.' }
    ]
  },
  cleric: {
    key: 'cleric',
    label: 'Cleric',
    templateKey: 'cleric',
    hitDie: 8,
    primaryAbilities: ['wisdom', 'constitution'],
    secondaryAbilities: ['strength', 'charisma'],
    savingThrows: ['wisdom', 'charisma'],
    skillChoices: ['History', 'Insight', 'Medicine', 'Persuasion', 'Religion'],
    skillCount: 2,
    armorProficiencies: ['light armor', 'medium armor', 'shields'],
    weaponProficiencies: ['simple weapons'],
    tools: [],
    spellcastingAbility: 'wisdom',
    levelOneFeatures: [
      { name: 'Spellcasting', description: 'You prepare and cast divine spells using Wisdom.' },
      { name: 'Divine Domain', description: 'Your divine calling grants domain-themed magic and gifts.' }
    ]
  },
  fighter: {
    key: 'fighter',
    label: 'Fighter',
    templateKey: 'fighter',
    hitDie: 10,
    primaryAbilities: ['strength', 'constitution'],
    secondaryAbilities: ['dexterity', 'wisdom'],
    savingThrows: ['strength', 'constitution'],
    skillChoices: ['Acrobatics', 'Animal Handling', 'Athletics', 'History', 'Insight', 'Intimidation', 'Perception', 'Survival'],
    skillCount: 2,
    armorProficiencies: ['light armor', 'medium armor', 'heavy armor', 'shields'],
    weaponProficiencies: ['simple weapons', 'martial weapons'],
    tools: [],
    spellcastingAbility: null,
    levelOneFeatures: [
      { name: 'Fighting Style', description: 'Specialized combat training grants a signature battle edge.' },
      { name: 'Second Wind', description: 'Regain hit points with a quick burst of resolve.' }
    ]
  },
  paladin: {
    key: 'paladin',
    label: 'Paladin',
    templateKey: 'paladin',
    hitDie: 10,
    primaryAbilities: ['strength', 'charisma'],
    secondaryAbilities: ['constitution', 'wisdom'],
    savingThrows: ['wisdom', 'charisma'],
    skillChoices: ['Athletics', 'Insight', 'Intimidation', 'Medicine', 'Persuasion', 'Religion'],
    skillCount: 2,
    armorProficiencies: ['light armor', 'medium armor', 'heavy armor', 'shields'],
    weaponProficiencies: ['simple weapons', 'martial weapons'],
    tools: [],
    spellcastingAbility: 'charisma',
    levelOneFeatures: [
      { name: 'Lay on Hands', description: 'Restore hit points from a pool of divine healing.' },
      { name: 'Divine Sense', description: 'Sense powerful celestial, fiendish, and undead presences.' }
    ]
  },
  ranger: {
    key: 'ranger',
    label: 'Ranger',
    templateKey: 'ranger',
    hitDie: 10,
    primaryAbilities: ['dexterity', 'wisdom'],
    secondaryAbilities: ['constitution', 'strength'],
    savingThrows: ['strength', 'dexterity'],
    skillChoices: ['Animal Handling', 'Athletics', 'Insight', 'Investigation', 'Nature', 'Perception', 'Stealth', 'Survival'],
    skillCount: 3,
    armorProficiencies: ['light armor', 'medium armor', 'shields'],
    weaponProficiencies: ['simple weapons', 'martial weapons'],
    tools: [],
    spellcastingAbility: 'wisdom',
    levelOneFeatures: [
      { name: 'Favored Enemy', description: 'You excel at tracking and fighting chosen foes.' },
      { name: 'Natural Explorer', description: 'Wilderness travel and awareness come naturally to you.' }
    ]
  },
  rogue: {
    key: 'rogue',
    label: 'Rogue',
    templateKey: 'rogue',
    hitDie: 8,
    primaryAbilities: ['dexterity', 'charisma'],
    secondaryAbilities: ['constitution', 'wisdom'],
    savingThrows: ['dexterity', 'intelligence'],
    skillChoices: ['Acrobatics', 'Athletics', 'Deception', 'Insight', 'Intimidation', 'Investigation', 'Perception', 'Performance', 'Persuasion', 'Sleight of Hand', 'Stealth'],
    skillCount: 4,
    expertiseCount: 2,
    armorProficiencies: ['light armor'],
    weaponProficiencies: ['simple weapons', 'hand crossbows', 'longswords', 'rapiers', 'shortswords'],
    tools: ["thieves' tools"],
    spellcastingAbility: null,
    levelOneFeatures: [
      { name: 'Expertise', description: 'Two trained skills become signature talents.' },
      { name: 'Sneak Attack', description: 'Deal extra precision damage when the opening is right.' },
      { name: "Thieves' Cant", description: 'You know the hidden code of the underworld.' }
    ]
  },
  wizard: {
    key: 'wizard',
    label: 'Wizard',
    templateKey: 'wizard',
    hitDie: 6,
    primaryAbilities: ['intelligence', 'constitution'],
    secondaryAbilities: ['dexterity', 'wisdom'],
    savingThrows: ['intelligence', 'wisdom'],
    skillChoices: ['Arcana', 'History', 'Insight', 'Investigation', 'Medicine', 'Religion'],
    skillCount: 2,
    armorProficiencies: [],
    weaponProficiencies: ['daggers', 'darts', 'slings', 'quarterstaffs', 'light crossbows'],
    tools: [],
    spellcastingAbility: 'intelligence',
    levelOneFeatures: [
      { name: 'Spellcasting', description: 'You prepare and cast arcane spells using Intelligence.' },
      { name: 'Arcane Recovery', description: 'Recover spell energy during a short rest once per day.' }
    ]
  }
});

export const STARTER_CLASS_KEYS = Object.freeze(Object.keys(STARTER_CLASS_OPTIONS));

export const SPECIES_OPTIONS_BY_RULESET = Object.freeze({
  '2014': {
    human: {
      key: 'human',
      label: 'Human',
      speed: 30,
      size: 'Medium',
      languages: ['Common'],
      abilityBonuses: {
        strength: 1,
        dexterity: 1,
        constitution: 1,
        intelligence: 1,
        wisdom: 1,
        charisma: 1
      },
      traits: [
        { name: 'Resourceful', description: 'Humans adapt quickly and thrive in almost any calling.' }
      ]
    },
    dwarf: {
      key: 'dwarf',
      label: 'Dwarf',
      speed: 25,
      size: 'Medium',
      languages: ['Common', 'Dwarvish'],
      abilityBonuses: { constitution: 2 },
      traits: [
        { name: 'Darkvision', description: 'Accustomed to life underground, you can see in the dark.' },
        { name: 'Dwarven Resilience', description: 'You are especially hardy against poison.' }
      ]
    },
    elf: {
      key: 'elf',
      label: 'Elf',
      speed: 30,
      size: 'Medium',
      languages: ['Common', 'Elvish'],
      abilityBonuses: { dexterity: 2 },
      traits: [
        { name: 'Darkvision', description: 'Your keen senses pierce dim light and darkness.' },
        { name: 'Fey Ancestry', description: 'Magic sleep cannot easily claim you, and charm is harder to stick.' }
      ]
    },
    halfling: {
      key: 'halfling',
      label: 'Halfling',
      speed: 25,
      size: 'Small',
      languages: ['Common', 'Halfling'],
      abilityBonuses: { dexterity: 2 },
      traits: [
        { name: 'Lucky', description: 'A streak of luck helps turn critical fumbles around.' },
        { name: 'Brave', description: 'Fear struggles to keep hold of you.' }
      ]
    }
  },
  '2024': {
    human: {
      key: 'human',
      label: 'Human',
      speed: 30,
      size: 'Medium',
      languages: ['Common'],
      abilityBonuses: {},
      traits: [
        { name: 'Resourceful', description: 'Humans adapt quickly and bring practical versatility to the group.' }
      ]
    },
    dwarf: {
      key: 'dwarf',
      label: 'Dwarf',
      speed: 30,
      size: 'Medium',
      languages: ['Common', 'Dwarvish'],
      abilityBonuses: {},
      traits: [
        { name: 'Darkvision', description: 'You can see through darkness with practiced underground sight.' },
        { name: 'Dwarven Toughness', description: 'Your people are known for staying power and stubborn grit.' }
      ]
    },
    elf: {
      key: 'elf',
      label: 'Elf',
      speed: 30,
      size: 'Medium',
      languages: ['Common', 'Elvish'],
      abilityBonuses: {},
      traits: [
        { name: 'Darkvision', description: 'You see clearly in dim light and darkness.' },
        { name: 'Fey Ancestry', description: 'Your bond to the fey makes charm and magical sleep harder to land.' }
      ]
    },
    halfling: {
      key: 'halfling',
      label: 'Halfling',
      speed: 30,
      size: 'Small',
      languages: ['Common', 'Halfling'],
      abilityBonuses: {},
      traits: [
        { name: 'Lucky', description: 'A lucky break often follows your worst stumbles.' },
        { name: 'Brave', description: 'Your courage stays steady under pressure.' }
      ]
    }
  }
});

export const BACKGROUND_OPTIONS = Object.freeze({
  acolyte: {
    key: 'acolyte',
    label: 'Acolyte',
    abilityChoices: ['wisdom', 'intelligence', 'charisma'],
    skills: ['Insight', 'Religion'],
    tools: [],
    languages: ['Celestial'],
    backstory: 'You spent years in sacred spaces, studying rites, duties, and the people who come seeking comfort.',
    originFeat: 'Magic Initiate (Divine)'
  },
  criminal: {
    key: 'criminal',
    label: 'Criminal',
    abilityChoices: ['dexterity', 'constitution', 'charisma'],
    skills: ['Deception', 'Stealth'],
    tools: ["thieves' tools"],
    languages: [],
    backstory: 'You learned to survive through secrets, leverage, and a practiced escape plan.',
    originFeat: 'Skilled'
  },
  outlander: {
    key: 'outlander',
    label: 'Outlander',
    abilityChoices: ['strength', 'dexterity', 'wisdom'],
    skills: ['Athletics', 'Survival'],
    tools: ['musical instrument'],
    languages: [],
    backstory: 'You grew up beyond the city walls, reading weather, tracks, and danger by instinct.',
    originFeat: 'Alert'
  },
  sage: {
    key: 'sage',
    label: 'Sage',
    abilityChoices: ['intelligence', 'wisdom', 'charisma'],
    skills: ['Arcana', 'History'],
    tools: ['calligrapher supplies'],
    languages: ['Draconic'],
    backstory: 'Books, maps, and careful note-taking shaped how you understand the world.',
    originFeat: 'Magic Initiate (Arcane)'
  },
  soldier: {
    key: 'soldier',
    label: 'Soldier',
    abilityChoices: ['strength', 'constitution', 'charisma'],
    skills: ['Athletics', 'Intimidation'],
    tools: ['gaming set'],
    languages: [],
    backstory: 'Military discipline taught you how to hold a line, follow a plan, and keep moving under fire.',
    originFeat: 'Tough'
  }
});

export const BACKGROUND_KEYS = Object.freeze(Object.keys(BACKGROUND_OPTIONS));

export const ALIGNMENT_OPTIONS = Object.freeze([
  'Lawful Good',
  'Neutral Good',
  'Chaotic Good',
  'Lawful Neutral',
  'True Neutral',
  'Chaotic Neutral'
]);

export const PERSONALITY_HOOKS = Object.freeze([
  'Keeps a steady voice when everyone else starts to panic.',
  'Believes every dangerous place hides one answer worth finding.',
  'Treats each fight like a problem to solve, not just a challenge to survive.',
  'Speaks kindly, but notices more than they let on.',
  'Carries quiet confidence earned the hard way.',
  'Finds humor in tense moments to keep the group moving.'
]);

export const NAME_BANKS = Object.freeze({
  human: {
    first: ['Aric', 'Brenna', 'Cassian', 'Elira', 'Galen', 'Maris', 'Nora', 'Rowan'],
    last: ['Ashford', 'Vale', 'Mercer', 'Stone', 'Thorne', 'Hawke', 'Winter', 'Dane']
  },
  dwarf: {
    first: ['Brom', 'Dagna', 'Elda', 'Gorin', 'Helja', 'Korin', 'Sigrid', 'Torra'],
    last: ['Ironvein', 'Stonehammer', 'Deepdelver', 'Fireforge', 'Goldmantle', 'Oakshield']
  },
  elf: {
    first: ['Aelar', 'Caelynn', 'Erevan', 'Lethariel', 'Nym', 'Soveliss', 'Thia', 'Vaeril'],
    last: ['Duskryn', 'Moonbrook', 'Silverbough', 'Starfall', 'Amakiir', 'Galanodel']
  },
  halfling: {
    first: ['Cade', 'Lidda', 'Milo', 'Poppy', 'Roscoe', 'Seraphina', 'Tobin', 'Wren'],
    last: ['Brushgather', 'Goodbarrel', 'Hilltopple', 'Tealeaf', 'Underbough', 'Willowrush']
  }
});

